import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  string,
  bool,
  object,
  shape,
  objectOf,
  number,
  arrayOf,
  oneOf,
} from "prop-types";

import cx from "classnames";

import initialState from "../data/initialState.js";

import fetchSearchResults from "../helpers/fetchSearchResults.js";
import {
  toggleFacet,
  initFacets,
  initCurrentParams,
  createQueryString,
} from "../helpers/dynamicSearch.js";

import KeywordSearch from "../components/KeywordSearch/KeywordSearch.jsx";
import Facets from "../Search/subcomponents/Facets.jsx";
import PromoResultsGrid from "./PromoResultsGrid.jsx";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner.jsx";
import Pagination from "../components/Pagination/Pagination.jsx";

const propTypes = {
  id: string.isRequired,
  theme: number,
  enableQueryString: bool,
  url: string.isRequired,
  dictionary: objectOf(string).isRequired,
  query: object.isRequired,
  sorters: arrayOf(
    shape({
      isActive: bool,
      value: string,
      direction: oneOf(["asc", "desc"]),
    }),
  ),
};

/**
 * PromoListingApp — the interactive layer for the "Promo Listing" component
 * (User Story 423684). It composes the existing search building blocks with no
 * changes to them:
 *   - KeywordSearch    : free-text search scoped to the listing's directory (the `q` param)
 *   - Facets           : the shared /episodes facet component (same markup/design), reused
 *                        here as a dropdown with Market + Solution sections
 *   - PromoResultsGrid : promo-style cards (image, date above title) in a 4-up grid
 *   - Pagination       : numbered paging (page 1, 2, 3 …)
 *
 * It hits the same /api/dynamiccontentlisting endpoint as DynamicContentListing, so
 * the sort (reverse-chronology) and taxonomy scoping are configured on the datasource.
 */
const PromoListingApp = (props) => {
  const {
    id,
    theme,
    enableQueryString,
    url,
    dictionary,
    query,
    sorters = [],
  } = props;

  const activeSorterParam = useMemo(() => {
    const activeSorter = sorters.find((sorter) => sorter.isActive) || null;

    return activeSorter
      ? {
          sortBy: activeSorter.value,
          sortOrder: activeSorter.direction,
        }
      : null;
  }, [sorters]);

  const generalParams = {
    ...initialState.query,
    ...query,
    ...activeSorterParam,
  };

  const [showOverlay, setShowOverlay] = useState(false);
  const [perPage, setPerPage] = useState(query.perPage || 4);
  const [facetsShown, setFacetsShown] = useState(false);
  const filterRef = useRef(null);
  // Close the filter dropdown on outside click / Esc. Kept lightweight — it does NOT set any
  // state on inside clicks, so it never re-renders the app on a checkbox click (which would
  // otherwise swallow the click and only leave keyboard toggling working).
  useEffect(() => {
    if (!facetsShown) return undefined;
    const onOutsideMouseDown = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFacetsShown(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setFacetsShown(false);
    };
    document.addEventListener("mousedown", onOutsideMouseDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutsideMouseDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [facetsShown]);

  const [selectedFacets, setSelectedFacets] = useState(
    initFacets(window.location.search),
  );
  const [serverData, setServerData] = useState(null);

  // initialize the query to that of the query string if it exists
  const [currentParams, setCurrentParams] = useState(
    initCurrentParams(window.location.search, generalParams),
  );

  const resultsFocusRef = useRef();
  const showFocusRef = useRef({ showFocus: false });

  // update the url with the latest query parameters
  useEffect(() => {
    if (enableQueryString) {
      const queryString = createQueryString(currentParams, selectedFacets);
      if (queryString.length > 0) {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
            window.location.pathname +
            "?" +
            createQueryString(currentParams, selectedFacets) +
            window.location.hash,
        );
      } else {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
            window.location.pathname +
            window.location.hash,
        );
      }
    }
  }, [currentParams, enableQueryString, selectedFacets]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setShowOverlay(true);

    fetchSearchResults({
      url,
      params: {
        ...currentParams,
        ...selectedFacets,
      },
      signal,
    })
      .then(function (data) {
        // Numbered pagination: replace results per page (no Load More appending).
        setServerData(data);
        setPerPage(parseInt(data.request.perPage, 10));
      })
      .catch(function (e) {
        if (e.name === "AbortError") {
          return; // Continuation logic has already been skipped, so return normally
        }
        console.warn("request error: " + e.message);
      })
      .finally(function () {
        setShowOverlay(false);
      });
    // unmounts or data changes
    return () => {
      controller.abort();
    };
  }, [currentParams, selectedFacets, url]);

  useEffect(() => {
    if (resultsFocusRef.current && showFocusRef.current.showFocus) {
      // move the focus back to the top of the new results on page change after new results are rendered.
      window.setTimeout(() => {
        resultsFocusRef.current.focus();
        showFocusRef.current.showFocus = false;
      }, 1000);
    }
  }, [serverData]);

  const handlePageChange = (page) => {
    showFocusRef.current.showFocus = true;
    setCurrentParams((prevState) => {
      return { ...prevState, page: page };
    });
  };

  // free-text search within this listing's directory. Reset to page 1 on a new query.
  const handleKeyword = useCallback((keywords) => {
    setCurrentParams((prevState) => {
      return { ...prevState, q: keywords === "" ? null : keywords, page: 1 };
    });
  }, []);

  const handleFacetChange = useCallback((facetGroupValue, facet) => {
    // analytics
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      type: "Promo Listing Facet Change",
      event: "dropdownClick",
      dropdownSelection: facet,
    });

    setSelectedFacets((prevState) => {
      return toggleFacet(facetGroupValue, facet, prevState);
    });
    // Reset to the first page whenever the filter selection changes.
    setCurrentParams((prevState) => ({ ...prevState, page: 1 }));
  }, []);

  // Numbered pagination: show whenever there is more than one page of results.
  const showPagination = serverData && serverData.totalResults > perPage;

  const focusItemNumber = 0;
  const showResults =
    serverData && serverData.results && serverData.results.length > 0;
  const showFeatured =
    serverData && serverData.featured && serverData.featured.length > 0;

  const hasFacets =
    serverData && serverData.facets && serverData.facets.length > 0;

  return (
    <div
      className={cx("promo-listing__app", {
        [`promo-listing__app--theme${theme}`]: theme,
      })}
      id={id}
    >
      <div className="promo-listing__controls">
        <div className="promo-listing__keyword-search">
          <KeywordSearch
            keywords={currentParams.q}
            handleEntry={handleKeyword}
            loading={showOverlay}
            dictionary={dictionary}
          />
        </div>
        {hasFacets && (
          <div className="promo-listing__filter" ref={filterRef}>
            <Facets
              serverData={serverData}
              selectedFacets={selectedFacets}
              dictionary={dictionary}
              mobileFacetsShown={facetsShown}
              setMobileFacetsShown={setFacetsShown}
              handleFacetChange={handleFacetChange}
              filteredSearch={true}
              resultsWrapperRef={resultsFocusRef}
              isGated={false}
            />
          </div>
        )}
      </div>
      {!showResults && !showFeatured && (
        <div className="promo-listing__no-results" aria-live="polite">
          {!showOverlay && (
            <span>{dictionary.noResultsMessage || "No Results Found"}</span>
          )}
        </div>
      )}
      {(showResults || showFeatured) && (
        <div
          className={cx("promo-listing__results", {
            "promo-listing__results--hide-summaries": !query?.showSummaries,
          })}
          id={`${id}-results`}
        >
          {showOverlay && <LoadingSpinner />}
          <div aria-live="polite" className="u-visuallyhidden">
            {!showOverlay && (
              <span>{dictionary.newResultsMessage || "New Results"}</span>
            )}
          </div>
          <PromoResultsGrid
            results={serverData.results}
            focusRef={resultsFocusRef}
            focusItem={focusItemNumber}
            dictionary={dictionary}
            showCta={query?.showCta}
          />
        </div>
      )}
      {showPagination && (
        <div className="promo-listing__pagination">
          <Pagination
            totalResults={serverData.totalResults}
            resultsPerPage={perPage}
            currentPage={currentParams.page}
            handlePageChange={(pageNum) => handlePageChange(pageNum)}
          />
        </div>
      )}
    </div>
  );
};

PromoListingApp.propTypes = propTypes;
export default PromoListingApp;
